import Button from 'antd/es/button';
import styled, { css } from 'styled-components';

import MyButton from '@components/Button';

// Common Style for date-range and single-date-range,

export const MOBILE_DATE_PICKER_FIXED_ACTION_CLEARANCE_PX = 75;
export const DESKTOP_SEARCHBAR_FOOTER_INSET_PX = 16;
export const DESKTOP_SEARCHBAR_CALENDAR_MAX_MONTH_HEIGHT_PX = 298;

export const DatePickerWrapper = styled.div<{
  contentHeight?: number;
  $desktopSearchbarFooter?: boolean;
}>`
  height: 100%;
  position: relative;

  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) and (stroke-color: transparent) {
      height: ${({ contentHeight }) => contentHeight + 'px'};
    }
  }

  .CalendarDay__selected {
    border: none;
    border-radius: 50%;
    background: ${({ theme }) => theme.palette.primaryBrand};
  }

  .CalendarDay__selected_start,
  .CalendarDay__selected_end {
    border-radius: 50%;
    position: relative;
    z-index: 0;

    &:before {
      display: block;
      position: absolute;
      content: '';
      left: 50%;
      right: 0;
      bottom: 0;
      top: 0;
      background-color: ${({ theme }) => theme.palette.primaryBrandSoft};
      z-index: -1;
    }

    &:after {
      display: block;
      position: absolute;
      content: '';
      left: 0;
      right: 0;
      bottom: 0;
      top: 0;
      background-color: ${({ theme }) => theme.palette.primaryBrand};
      z-index: -1;
      border-radius: 50%;
    }
  }

  .CalendarDay__selected_end {
    &:before {
      transform: translateX(calc(-100% - 1px));
    }
  }

  .CalendarDay__selected_span {
    border: none;
    background-color: ${({ theme }) => theme.palette.primaryBrandSoft};
    color: ${({ theme }) => theme.palette.black};
  }

  .CalendarDay__hovered_span {
    background-color: ${({ theme }) => theme.palette.primaryBrandSoft};
    border-radius: 50% !important;
    color: ${({ theme }) => theme.palette.black};
  }

  .CalendarDay__default {
    border: none;
    vertical-align: middle;

    &:not(.CalendarDay__blocked_out_of_range):hover {
      border-radius: 50%;
      background-color: ${({ theme }) => theme.palette.primaryBrand};
      color: white;
    }
  }

  .CalendarDay__selected_start [data-datepicker-day-text],
  .CalendarDay__selected_end [data-datepicker-day-text],
  .CalendarDay__selected [data-datepicker-day-text],
  .CalendarDay__default:not(.CalendarDay__blocked_out_of_range):not(
      [aria-disabled='true']
    ):not(.CalendarDay__selected):not(.CalendarDay__selected_span):not(
      .CalendarDay__hovered_span
    ):hover
    [data-datepicker-day-text] {
    color: ${({ theme }) => theme.palette.white} !important;
  }

  .CalendarDay__hovered_span.CalendarDay__default:not(
      .CalendarDay__blocked_out_of_range
    ):not([aria-disabled='true']):not(.CalendarDay__selected_span):hover
    [data-datepicker-availability-day-text] {
    color: ${({ theme }) => theme.palette.white} !important;
  }

  .CalendarDay__selected_span:last-of-type {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }

  .CalendarDay__selected_span:first-of-type {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
  }

  .CalendarMonth_caption {
    font-size: 14px;
    margin-top: 5px;
  }

  .DayPicker,
  .DayPicker_1,
  .DayPicker__horizontal,
  .DayPicker__horizontal_2 {
    width: 100% !important;
  }

  .CalendarDay__blocked_out_of_range,
  .CalendarDay__blocked_out_of_range:active,
  .CalendarDay__blocked_out_of_range:hover {
    pointer-events: none;
    background: ${({ theme }) => theme.palette.white};
    opacity: 0.5;
    color: ${({ theme }) => theme.palette.iron};
    cursor: not-allowed;
  }

  ${({ theme, $desktopSearchbarFooter }) =>
    $desktopSearchbarFooter &&
    css`
      overflow: hidden;
      border-radius: 8px;
      background-color: ${theme.palette.white};
      box-shadow: 0 4px 5px rgba(23, 43, 77, 0.3);

      .DayPicker__withBorder {
        border-radius: 0;
        box-shadow: none;
      }

      /* React Dates can retain a five-week inline height after navigating back
         to a six-week month. Reserve the maximum horizontal month height so
         its own transition overflow never clips the final week. */
      .DayPicker_transitionContainer {
        min-height: ${DESKTOP_SEARCHBAR_CALENDAR_MAX_MONTH_HEIGHT_PX}px;
      }
    `}
`;

// CORR-3b: the weekend (Ct/Pz) day-of-week header accent (Figma node 97-925,
// "Takvim" tab, #F4BC4C ~ GSF yellow400) is HOTEL-ONLY (DateRangePicker). It
// must NOT leak into SingleDatePicker (carRental/flight, IK-3 out of scope),
// so it lives in this styled EXTENSION of `DatePickerWrapper` — used only by
// `DateRangePicker.tsx` — rather than in the shared base above. Extending
// styled-components generates an additional, wrapper-specific class name, so
// the nested `.DayPicker_weekHeader_li` selector below can only ever match
// when rendered through `DateRangePickerWrapper`, never through the plain
// `DatePickerWrapper` used by `SingleDatePicker`.
export const DateRangePickerWrapper = styled(DatePickerWrapper)<{
  $holidayMode?: boolean;
  $showHolidayModeShadow?: boolean;
  $desktopSearchbarFooter?: boolean;
}>`
  ${({ theme, $holidayMode, $showHolidayModeShadow }) =>
    $holidayMode &&
    css`
      overflow: hidden;
      border-radius: 8px;
      background-color: ${theme.palette.white};
      box-shadow: ${$showHolidayModeShadow !== false
        ? '0 4px 5px rgba(23, 43, 77, 0.3)'
        : 'none'};

      @media (max-width: ${theme.breakPoint.LARGE_1X}) {
        box-shadow: none;
      }

      .DayPicker__withBorder {
        box-shadow: none;
        border-radius: 0;
      }

      .CalendarDay__default {
        color: ${theme.palette.grey1100};
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
      }

      .CalendarDay__default:not(.CalendarDay__blocked_out_of_range):hover {
        color: ${theme.palette.white};
        border-radius: 50%;
        background-color: ${theme.palette.primaryBrand};
      }

      .CalendarDay__default:not(.CalendarDay__blocked_out_of_range) :hover {
        color: inherit;
        background-color: transparent;
      }

      .CalendarDay__selected_start,
      .CalendarDay__selected_end {
        color: ${theme.palette.white};
      }

      .CalendarDay__blocked_out_of_range,
      .CalendarDay__blocked_out_of_range:active,
      .CalendarDay__blocked_out_of_range:hover {
        color: ${theme.palette.NL80};
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        opacity: 1;
      }

      .DayPicker_weekHeader_li {
        color: ${theme.palette.grey1100};
      }

      .DayPicker_weekHeader_li:nth-last-child(-n + 2) {
        color: ${theme.palette.yellow400};
      }

      @media (max-width: ${theme.breakPoint.LARGE_1X}) {
        .DayPicker_transitionContainer__verticalScrollable {
          padding-bottom: ${MOBILE_DATE_PICKER_FIXED_ACTION_CLEARANCE_PX}px;
        }
      }
    `}
`;

const NavButtonMixin = css`
  position: absolute;
  top: 20px;
`;

// TODO add "link" type button to setur-button component, then replace antd Button with ui-kit'
// import Button from '@components/Button';
export const NavPrevButton = styled(Button)`
  &&& {
    ${NavButtonMixin}
    left: 18px;
  }
`;

export const NavNextButton = styled(Button)`
  &&& {
    ${NavButtonMixin}
    right: 18px;
  }
`;

export const ResponsiveNavDownButton = styled(Button)`
  &&& {
    ${NavButtonMixin}
    width: 100%;
    height: 40px;
    z-index: 200;
    cursor: pointer !important;
    user-select: none !important;
    margin: 0px !important;
    border-width: 1px 1px 1px 0px !important;
    border-top-style: solid !important;
    border-right-style: solid !important;
    border-bottom-style: solid !important;
    border-top-color: rgb(228, 231, 231) !important;
    border-right-color: rgb(228, 231, 231) !important;
    border-bottom-color: rgb(228, 231, 231) !important;
    border-image: initial !important;
    color: rgb(117, 117, 117) !important;
    padding: 0 !important;
    background: rgb(255, 255, 255) !important;
    box-shadow: rgb(0 0 0 / 10%) 0px 0px 5px 2px !important;
    position: relative !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    height: 100% !important;
    border-left-style: initial !important;
    border-left-color: initial !important;
    width: 100% !important;
  }
`;

export const ResponsiveDatePickerWrapper = styled.div`
  /* max-width: 420px; */
`;

export const DateRangePickerFooter = styled.div<{
  $holidayMode?: boolean;
  $desktopSearchbarFooter?: boolean;
  $holidaySummaryVisible?: boolean;
}>`
  width: 100%;
  height: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter ? '70px' : '64px'};
  display: flex;
  align-items: center;
  padding: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter ? '15px 16px 16px' : '0 16px'};
  justify-content: ${({
    $holidayMode,
    $desktopSearchbarFooter,
    $holidaySummaryVisible,
  }) =>
    $holidaySummaryVisible
      ? 'space-between'
      : $holidayMode || $desktopSearchbarFooter
      ? 'flex-end'
      : 'space-between'};
  color: ${({ theme }) => theme.palette.grey1100};
  background-color: ${({ theme }) => theme.palette.white};
  border-top: none;
  ${({ theme, $desktopSearchbarFooter }) =>
    $desktopSearchbarFooter &&
    css`
      position: relative;
      margin-top: 16px;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        right: ${DESKTOP_SEARCHBAR_FOOTER_INSET_PX}px;
        left: ${DESKTOP_SEARCHBAR_FOOTER_INSET_PX}px;
        border-top: 1px solid ${theme.palette.NL30};
      }
    `}
  box-shadow: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter
      ? 'none'
      : '0 2px 6px rgba(0, 0, 0, 0.05), 0 1px 0 1px rgba(0, 0, 0, 0.07)'};
  border-bottom-left-radius: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter ? '0' : '3px'};
  border-bottom-right-radius: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter ? '0' : '3px'};
`;

export const StyledButton = styled(MyButton)<{
  $holidayMode?: boolean;
  $desktopSearchbarFooter?: boolean;
}>`
  width: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $desktopSearchbarFooter ? '92px' : $holidayMode ? '148px' : '76px'};
  height: ${({ $holidayMode, $desktopSearchbarFooter }) =>
    $holidayMode || $desktopSearchbarFooter ? '38px' : '32px'};
  align-items: center;
  justify-content: center;
  display: flex;

  ${({ $desktopSearchbarFooter }) =>
    $desktopSearchbarFooter &&
    css`
      &&& {
        width: 92px;
        min-width: 92px;
        height: 38px;
        padding: 10px 24px;
        border-color: #303138 !important;
        border-radius: 8px;
        background-color: #303138 !important;
      }
    `}
`;

export const CalendarHolidayFooterSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const CalendarHolidayFooterItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const CalendarHolidayFooterDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  flex: none;
  background-color: ${({ theme }) => theme.palette.coral};
`;

export const DatePickerMonthLabel = styled.div<{ $holidayMode?: boolean }>`
  color: ${({ theme }) => theme.palette.grey1100};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  ${({ $holidayMode }) =>
    $holidayMode &&
    css`
      font-weight: 700;
      line-height: 18px;
    `}
`;

// Color intentionally omitted — inherited via CSS cascade from the ancestor
// `.DayPicker_weekHeader_li` element. That rule (and the Ct/Pz weekend accent,
// CORR-3/CORR-3b) only exists on `DateRangePickerWrapper` (hotel), so this
// label keeps its default color when rendered inside the plain
// `DatePickerWrapper` used by `SingleDatePicker`.
export const DatePickerDayOfWeekLabel = styled.div<{
  $holidayMode?: boolean;
}>`
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  ${({ $holidayMode }) =>
    $holidayMode &&
    css`
      font-weight: 700;
      line-height: 18px;
    `}
`;

// Holiday marking (CMS-driven calendar holidays) — mapped to GSF design tokens.
// Consumed by DateRangePicker/SingleDatePicker holiday tabs (GSF-WEB-003/004/007).

export const Holiday = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, 1fr);
  background-color: ${({ theme }) => theme.palette.white};
  padding: 16px;
  cursor: default;

  @media (max-width: ${({ theme }) => theme.breakPoint.LARGE_1X}) {
    grid-template-columns: 1fr;
  }
`;

export const HolidayCard = styled.div`
  min-height: 72px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.NL40};
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
`;

export const WeekHoliday = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  cursor: default;
`;

export const WeekHolidayCard = styled.div`
  min-height: 33px;
  width: max-content;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.NL40};
  padding: 8px;
  display: flex;
  align-items: flex-end;
  column-gap: 3px;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grey1100};
  background-color: ${({ theme }) => theme.palette.white};
  cursor: pointer;
`;

export const HolidayContainer = styled.div`
  align-items: flex-start;
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

export const HolidayName = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: ${({ theme }) => theme.palette.grey1100};
`;

// --- Holiday tab-panel content (MAP-takvim-guncelleme / CORR-2) ------------
// The "Resmi Tatiller" card grid and "Hafta Sonu Tatilleri" chip grid rendered
// inside the holiday tab panels. Ports the Setur reference layout, adapted to
// GSF design tokens; typography inherits the project's global body font.

// Scroll wrapper so long holiday lists stay inside the picker without pushing
// the footer out of view.
export const HolidayPanelScroll = styled.div`
  max-height: 360px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.palette.white};
`;

// "{numberOfDays} Gün" badge shown next to the holiday name. Figma (node
// 97-1084) uses a cream/gold fill (rgba(244,188,76,0.3) ~ GSF yellow200) with
// dark neutral text (#2F3137 = GSF grey1100), NOT the coral brand color.
export const HolidayDaysBadge = styled.span`
  flex: none;
  padding: 4px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 15px;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.grey1100};
  background-color: ${({ theme }) => theme.palette.yellow200};
`;

export const HolidayDescriptionBadges = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

export const HolidayDescriptionBadge = styled(HolidayDaysBadge)`
  align-self: flex-start;
`;

export const HolidayCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5px;
  width: 100%;
`;

// Bottom row of a holiday card: the date range plus the chevron affordance.
export const HolidayCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

export const HolidayDateRange = styled.span`
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.grey1100};
`;

export const HolidayChevron = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.grey1100};
`;

// A month group in the "Hafta Sonu Tatilleri" tab (label + chip row), laid out
// as a ROW (Figma node 97-1230: fixed-width month label on the left, chips
// flowing to the right on the same line). Vertical spacing between groups is
// handled by `WeekHoliday`'s own `gap`.
export const WeekHolidayMonthGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const WeekHolidayMonthHeader = styled.div`
  flex: none;
  width: 60px;
  padding: 6px 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.grey1100};
`;

// Weekend chip text is split into two spans matching Figma (97:1777): the
// day-range digits are SemiBold, the trailing month name is Regular. Both
// inherit `WeekHolidayCard`'s 12px/grey1100 base; only the weight differs.
export const WeekHolidayChipBold = styled.span`
  font-weight: 600;
`;

export const WeekHolidayChipRegular = styled.span`
  font-weight: 400;
`;

export const WeekHolidayChipRow = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 8px;
`;

// Per-cell holiday marker (GSF-WEB-006 follow-up). Unlike the Holiday/WeekHoliday
// tab-panel containers above, this is the small ~4px dot rendered beneath a
// calendar day number (Setur reference: `CalenderEventDay`). It is injected into
// `renderCalendarEventFast` (GSF-WEB-005) so both pickers share one contract.
export const HolidayDayContent = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

// Official holiday (Resmi Tatil) cell dot — Figma Brand/BR300 (#FA5851), which
// maps 1:1 to the existing GSF `coral` theme token.
export const HolidayDot = styled.span`
  position: absolute;
  bottom: 2px;
  left: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.coral};
  transform: translateX(-50%);
`;

// Weekend holiday (Hafta Sonu) cell dot — Figma Teal/T400, mapped to the GSF
// `teal` theme token (#00A3BF, added in MAP-takvim-guncelleme). Reuses
// HolidayDot's size/position, overriding only the color.
export const WeekHolidayDot = styled(HolidayDot)`
  background-color: ${({ theme }) => theme.palette.teal};
`;

// Holiday tab toggle (MAP-takvim-guncelleme / CORR-1). Segmented radio-pill
// toggle rendered above the calendar when holiday data is available. Ports the
// Setur reference pattern (Wrapper/CheckboxContainer/RadioButton/RadioText),
// adapted to GSF design tokens.
export const HolidayTabBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 12px 0 12px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    gap: 4px;
    padding: 10px 12px;
  }
`;

export const HolidayTab = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  height: 34px;
  flex: 1;
  min-width: 0;
  padding: 8px 6px;
  gap: 2px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.palette.grey1100 : theme.palette.NL40};
  background-color: ${({ theme, $active }) =>
    $active ? theme.palette.NL20 : 'transparent'};

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    flex: 0 1 auto;
    padding: 4px 6px;
    gap: 3px;
  }

  input[type='radio']:not(old) {
    display: inline-block;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 1em;
    box-sizing: border-box;
    border: 1.5px solid ${({ theme }) => theme.palette.grey1100};
    background-color: transparent;
  }

  input[type='radio']:not(old):checked {
    background-image: radial-gradient(
      circle at center,
      ${({ theme }) => theme.palette.grey1100} 0,
      ${({ theme }) => theme.palette.grey1100} 3.75px,
      transparent 4px,
      transparent 100%
    );
  }
`;

export const HolidayTabRadio = styled.input`
  width: 18px;
  height: 18px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
  }
`;

// `$active` kept for JSX call-site compatibility (DateRangePicker.tsx still
// passes it); font-weight no longer varies by tab state (Figma keeps the label
// Regular/400 whether active or not, MAP CORR-5).
export const HolidayTabLabel = styled.div<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0;
  color: ${({ theme }) => theme.palette.grey1100};
  white-space: nowrap;
  overflow: visible;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    flex: 0 1 auto;
    font-size: 12px;
    line-height: 16px;
  }
`;
